import React, { useState } from 'react';
import Button from '../../generic_components/Button/Button'; // Adjust path as needed
import Input from '../../generic_components/Input/Input'; // Adjust path as needed
import { useToast } from '../../generic_components/Toast/Toast'; // Adjust path as needed
import { url } from '../../api/endpointURL';
import { API } from '../../api';
import Modal from '../../generic_components/Modal/Modal';

const MatterportConfig = ({ hwid, site, setTags, setModelId, setShowMatterportModal, handleCloseMatterportModelConfig }) => {
  const [matterportUrl, setMatterportUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingMapping, setExistingMapping] = useState(null); // To handle existing mapping scenario

  const toast = useToast();

  // Extract model ID from Matterport URL
  const extractModelId = (url) => {
    try {
      // Handle different Matterport URL formats
      // https://my.matterport.com/show/?m=HH4zTp41aTr
      // https://my.matterport.com/show?m=HH4zTp41aTr
      const urlObj = new URL(url);
      
      // Check if it's a valid Matterport domain
      if (!urlObj.hostname.includes('matterport.com')) {
        throw new Error('Invalid Matterport URL. Please use a URL from my.matterport.com');
      }

      // Extract the 'm' parameter
      const modelId = urlObj.searchParams.get('m');
      
      if (!modelId) {
        throw new Error('Model ID not found in URL. Please ensure the URL contains the "m" parameter.');
      }

      // Validate model ID format (typically alphanumeric)
      if (!/^[a-zA-Z0-9_-]+$/.test(modelId)) {
        throw new Error('Invalid model ID format');
      }

      return modelId;
    } catch (err) {
      if (err instanceof TypeError) {
        throw new Error('Invalid URL format. Please enter a valid Matterport URL.');
      }
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Extract model ID from URL
      const modelId = extractModelId(matterportUrl);

      // Post to backend
      const response = await fetch(`${url}/matterport/model-site-mapping`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          hwid: hwid,
          modelId: modelId,
          siteName: site
        }),
      });

        console.log('Error response from backend:', response);

      if (response.status !== 200) {
      
        const errorData = await response.json();
        if (response.status === 409) { // Conflict error, likely due to existing mapping  
          console.log('Existing mapping found:', errorData); 
          setExistingMapping(errorData.existingMapping);
          return;
        }
        throw new Error(errorData.message || 'Failed to configure Matterport model');
      }

    const data = await response.json();

    if (!data.success) {     
      throw new Error(data.message || 'Failed to configure Matterport model');      
    }
    const successMessage = `Successfully mapped site ${site} to Matterport model ${modelId}`;

    toast.success(successMessage, {
        autoClose: 5000
      });
    
    setMatterportUrl('');

    await setMatterportTags(modelId);
    setModelId(modelId);
    setShowMatterportModal(true);
    handleCloseMatterportModelConfig();
      
    } catch (err) {
      toast.error(err.message, {
        autoClose: 5000
      })
    } finally {
      setLoading(false);
    }
  };

  // patch model name with site name
  const updateModelName = async (modelId, siteName) => {
    if (!modelId || !siteName) {
      console.warn('Model ID or Site Name is missing. Skipping model name sync.');
      return;
    }
    try {
      await fetch(`${url}/matterport/update-model-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, siteName })
      });      
    }
    catch (err) {
      console.error('❌ Model name update failed:', err);
    }
    
  }

  const handleUrlChange = (e) => {
    const modelUrl = e.target.value;
    setMatterportUrl(modelUrl);
    const modeId = extractModelId(modelUrl);
    if (modeId) {
      // update mode name as site name
      updateModelName(modeId, site);
    }
  };

    // set Matterport tags given matterport model id
    const setMatterportTags = async(matterPortModelId) => {
    try {
      // fetch matterport tags tied matterport model id form backend api
      const response = await API.get(`/matterport/tags/${encodeURIComponent(matterPortModelId)}`);
      if (response.status === 200) {
        const data = response.data;
        // Assuming the response contains { tags: [...] }
        const tags = data.tags;
        setTags(tags);
      }
    } catch (error) {
      console.error('Error fetching Matterport tags:', error);
    }
  }

  // Unmap the selected Model from the mapped site
  const handleMappterportUnmap = async() => {
    try {
      const payload = {
          hwid: existingMapping.hwid,
          modelId: existingMapping.modelId,
          siteName: existingMapping.site
        };
      const response = await API.post('/matterport/model-site-unmapping', payload);
      if (response.status === 200) {
        const successMsg = response.data.message || `Successfully unmapped Matterport model - ${existingMapping.modelId} from site - ${existingMapping.site}`;
        toast.success(successMsg, { autoClose: 5000 });
       setExistingMapping(null);
      } else {
        const errorMsg = response.data.message || 'Failed to unmap Matterport model from site';
        toast.error(errorMsg, { autoClose: 5000 });
      }
    } catch (error) {
      console.error('Error unmapping Matterport model from site:', error);
      toast.error(`An error occurred while unmapping the Matterport model from site ${existingMapping.site}`, { autoClose: 5000 });
    }
  }

  const handleCloseUnmapModel = () => {
    // reset mode name with existing mapping if devices to not proceed with new mapping
    if (existingMapping) {
      updateModelName(existingMapping.modelId, existingMapping.site);
    }
    setExistingMapping(null);
  }
  

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <Input
          label="Matterport Model URL"
          type="text"
          value={matterportUrl}
          onChange={handleUrlChange}
          placeholder="https://my.matterport.com/show/?m=HH4zTp41aTr"
          required={true}
          disabled={loading}
          size="md"
        />
        
        <div style={{ fontSize: '0.875rem', color: '#6c757d', marginTop: '0.25rem', marginBottom: '1rem' }}>
          Enter the full Matterport model URL (e.g., https://my.matterport.com/show/?m=HH4zTp41aTr). Refer Matterport cloud portal to get the model URL.
        </div>       

        <div className="d-flex justify-content-end gap-2">
          <Button 
            variant="gradient" 
            type="submit" 
            disabled={loading || !matterportUrl.trim()}
          >
            {loading ? 'Configuring...' : 'Configure'}
          </Button>
        </div>
      </form>
      
      {/* Model to show existing mapping warning and action to unmap existing mapping if user wants to proceed with new mapping */}
      <Modal size='md' show={!!existingMapping} onHide={handleCloseUnmapModel} position='center'>
        <Modal.Header closeButton>
          <Modal.Title>Unmap 3D Model from Site</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='matterport-unmap-warning-text'>This Model is already mapped to site <span style={{ fontWeight: 'bold', color: '#F89532' }}>{existingMapping?.site}</span>. Do you want to remove this mapping?</div>
          <div className='matterport-unmap-warning-text'>This action will remove the association between the model and the site.</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1em' }}>
            <Button variant='danger' onClick={handleMappterportUnmap}>Yes, Unmap</Button>
            <Button variant='ghost' onClick={handleCloseUnmapModel}>Cancel</Button>
          </div>
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default MatterportConfig;